import React from 'react';
import { createPortal } from 'react-dom';
import MobileSearchDock from 'screens/Main/Search/Components/MobileSearchDock';
import MobileFilterSheet from 'screens/Main/Search/Components/MobileFilterSheet';
import { CardFilterStateApi } from './cardFilterUrlState';
import { TraderBalanceField } from './FilterPanel';
import { useLocalTextValue } from './LocalTextInput';

interface CardFilterMobileProps {
  filters: CardFilterStateApi;
  /** Live matching count for the sheet's "Show N cards" button. */
  totalCount?: number;
  /**
   * The bottom dock with the Filters button. Off on the profile Want List tab
   * while a trade is in progress: the cart bar owns the bottom edge there and
   * carries the Filters button itself, so the two never stack (decision 14).
   */
  showDock?: boolean;
  sheetOpen: boolean;
  onSheetOpenChange: (open: boolean) => void;
  /** Own lists only, same rule as the rail. */
  hideTradeSection?: boolean;
  /**
   * Group to scroll into view when the sheet opens (MobileFilterSheet's
   * scrollToGroupId). The My Cards magnifying glass passes the Card text group.
   */
  sheetScrollToGroupId?: string;
  /** Activity only: Trader balance under Point value in the sheet. */
  traderBalance?: TraderBalanceField;
  /** Override for the sheet's "Clear all filters" when the page holds filter state outside the URL. */
  onClearAll?: () => void;
  /** Extra class on the dock, for a page whose gutter differs from the dock's 12px default. */
  dockClassName?: string;
}

/**
 * Phone-width mount of the shared panel: Search's MobileSearchDock and
 * MobileFilterSheet reused as they are. The sheet renders FilterPanel in its
 * touch variant, so Color identity is ManaFilters variant="pips" size 52.
 */
export const CardFilterMobile: React.FC<CardFilterMobileProps> = ({
  filters,
  totalCount,
  showDock = true,
  sheetOpen,
  onSheetOpenChange,
  hideTradeSection = false,
  sheetScrollToGroupId,
  traderBalance,
  onClearAll,
  dockClassName,
}) => {
  // The dock's field is bound to URL-derived state; keep the typed text local
  // so a keystroke never races the URL round-trip (see LocalTextInput).
  const dockName = useLocalTextValue(filters.state.name, (name) => filters.update({ name }));
  return (
  <>
    {/* Portalled to body like CardFilterRail and the sheet: the dock is
        position: fixed, and the profile's main card has a backdrop-filter,
        which makes the card (not the viewport) the containing block for any
        fixed descendant. Mounted inline, the dock pinned itself to the bottom
        of the card, directly over the Load More button, and was off screen
        while the list scrolled. */}
    {showDock &&
      createPortal(
        <MobileSearchDock
          value={dockName.local}
          onChange={dockName.onChange}
          activeFilterCount={filters.tokens.length + (traderBalance && (traderBalance.min || traderBalance.max) ? 1 : 0)}
          onOpenFilters={() => onSheetOpenChange(true)}
          className={dockClassName}
        />,
        document.body,
      )}
    {sheetOpen && (
      <MobileFilterSheet
        state={filters.state}
        onChange={filters.update}
        onClearAll={onClearAll ?? filters.clearAll}
        onClose={() => onSheetOpenChange(false)}
        totalCount={totalCount}
        hideTradeSection={hideTradeSection}
        scrollToGroupId={sheetScrollToGroupId}
        traderBalance={traderBalance}
      />
    )}
  </>
  );
};

export default CardFilterMobile;
